import { useAppStore } from '../appStore';
import { useIsMissionModerator } from '../../hooks/useIsMissionModerator';
import { useIsSelfServicedPilot } from '../../hooks/useIsSelfServicedPilot';
import { useEffect } from 'react';
import { UploadData, useUploadStore } from '../uploadStore';
import { UploadType } from '../../consts/uploadType';
import { UploadOverview } from '../../types';
import { substrDateFromAirtableFormat } from '../../components/main_container/missions/helpers/date';
import { PlacementServiceStatusAirtable } from '../../consts/placementServiceStatusAirtable';
import { createGcpRequestUploadState } from '../../components/main_container/missions/openMission/gcpRequestUpload/useCreateOrRestoreGcpRequestState';

/**
 * For TA-pilots (whose e-mail matches a UAS Pilots record by "Pilot Emails Plain List") we restore uploads only by scheduleId
 * For self-serviced pilots we only restore uploads without a scheduleId (also the backend only sends interrupted uploads)
 * For Moderators we restore all uploads
 */
export const useRestoreUploads = () => {
  const isMissionModerator = useIsMissionModerator();
  const isSelfServiced = useIsSelfServicedPilot();
  const { setUpload, setGcpRequest } = useUploadStore();
  const { missions } = useAppStore();

  useEffect(() => {
    if (isMissionModerator === null || isSelfServiced === null || missions === undefined) {
      return;
    }

    const restoreScheduled = isMissionModerator || !isSelfServiced;
    const restoreNonScheduled = isMissionModerator || isSelfServiced;

    missions?.forEach((customer) => {
      customer.sites.forEach((site) => {
        if (site.uploads) {
          for (const [type, uploads] of Object.entries(site.uploads)) {
            if (!uploads.length) return;
            const isPano = type === UploadType.Pano;

            const toRestore: UploadOverview[] = [];

            if (restoreScheduled) {
              // The backend allows to create multiple uploads for different dates (but removes duplicates for the same day),
              // the old UI didn't show DONE uploads, so we can now have multiple DONE uploads for the same scheduleId on
              // different dates.
              // Panos are tied to schedules, but can have different dates, so we can't do anything about it.
              // This problem should eventually disappear, so we can probably ignore it.
              const scheduledFlightDates = site.scheduledFlights.map(({ flightDateIso }) =>
                substrDateFromAirtableFormat(flightDateIso),
              );
              uploads
                .filter(
                  ({ flightScheduleId, date }) => flightScheduleId && (scheduledFlightDates.includes(date) || isPano),
                )
                .forEach((upload) => toRestore.push(upload));
            }

            if (restoreNonScheduled) {
              const newest = uploads
                .filter(({ flightScheduleId }) => !flightScheduleId)
                .sort((u1, u2) => u2.date.localeCompare(u1.date))[0];
              if (newest) {
                toRestore.push(newest);
              }
            }

            toRestore.forEach((upload) => {
              const abortController = new AbortController();
              const uploadData: UploadData = {
                upload: undefined,
                queue: [],
                overview: upload,
                meta: {
                  abortController,
                  site,
                  type: type as UploadType,
                  restoringUpload: false,
                  currentBatch: -1, // This will be updated when user opens a mission
                  isPreviouslyCompleted: upload.photosCount.uploaded === upload.photosCount.all,
                },
              };

              setUpload(isPano, customer.id, site.id, upload.flightScheduleId, uploadData);
            });
          }
        }

        if (site.gcpPlacementServices.length) {
          // If there are more than 1 record (which normally shouldn't happen), we will restore the one pilot has already submitted
          const mostRelevantGcpPlacementService = site.gcpPlacementServices.find(
            ({ status }) => status === PlacementServiceStatusAirtable.ToBeProcessedByGis,
          );
          const gcpPlacementService = mostRelevantGcpPlacementService ?? site.gcpPlacementServices[0];

          const gcpRequest = createGcpRequestUploadState(gcpPlacementService);

          setGcpRequest(customer.id, site.id, gcpRequest);
        }
      });
    });
  }, [missions, isMissionModerator, isSelfServiced]);
};
